import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, skip, switchMap } from 'rxjs/operators';
import { DestinationsApiService, DestinationDto, DestinationTypeDto } from '../../services/destinations-api.service';
import { AuthService, SessionUser } from '../../services/auth.service';
import { NotificationService } from '../../services/notification.service';
import { DestinationView, DestinationStatus } from './destination.types';
import { DestinationsFormComponent } from './create/destinations-form.component';
import { EntityOwnerService } from '../../services/entity-owner.service';
import { CreationStateService } from '../../services/creation-state.service';
import { loadSearch, saveSearch } from '../../utils/persisted-search';
import { SearchClearDirective } from '../shared/search-clear/search-clear.directive';

@Component({
  selector: 'app-destinations',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, DestinationsFormComponent, SearchClearDirective],
  templateUrl: './destinations.component.html',
  styleUrls: ['./destinations.component.scss']
})
export class DestinationsComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  @ViewChild(DestinationsFormComponent) destinationFormComponent?: DestinationsFormComponent;

  destinations: DestinationView[] = [];
  filteredDestinations: DestinationView[] = [];
  destinationTypes: DestinationTypeDto[] = [];

  readonly statusColors: Record<DestinationStatus, string> = {
    active: '#22c55e',
    inactive: '#9ca3af'
  };

  readonly availableStatuses: { id: DestinationStatus; labelKey: string }[] = [
    { id: 'active', labelKey: 'destinations.filters.status.active' },
    { id: 'inactive', labelKey: 'destinations.filters.status.inactive' }
  ];

  isLoading = false;
  loadingError: string | null = null;
  searchTerm = loadSearch('reach/destinations');
  statusFilters: Record<DestinationStatus, boolean> = this.createStatusFilters();
  pageSizeOptions = [10, 25, 50];
  pageSize = 10;
  currentPage = 0;
  panelOpen = false;
  formMode: 'create' | 'edit' = 'create';
  currentUser: SessionUser | null = null;
  readonly locale = 'it-IT';
  private ownerLabels = new Map<string, string>();
  activeActionMenuId: number | null = null;
  activeActionItem: DestinationView | null = null;
  actionMenuPosition = { top: 0, left: 0 };
  actionMenuArrowOffset = 24;

  constructor(
    private destinationsApi: DestinationsApiService,
    private authService: AuthService,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private entityOwnerService: EntityOwnerService,
    private creationStateService: CreationStateService
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUser = user;
      })
    );
    this.subscriptions.add(
      this.entityOwnerService.owners$.subscribe(map => {
        this.ownerLabels = map;
      })
    );
    this.subscriptions.add(
      this.authService.selectedDcr$
        .pipe(distinctUntilChanged(), skip(1), switchMap(() => this.authService.waitForEntitySync()))
        .subscribe(() => this.loadDestinationTypes())
    );
    this.authService.waitForEntitySync().subscribe(() => this.loadDestinationTypes());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // Ensure entity selector is always re-enabled when component is destroyed
    this.creationStateService.setCreationMode(false);
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const clickedMenu = !!target?.closest('.destinations__action-menu');
    const clickedTrigger = !!target?.closest('.destinations__action-trigger');
    if (!clickedMenu && !clickedTrigger) {
      this.closeActionMenu();
    }
  }

  loadDestinationTypes(): void {
    const sub = this.destinationsApi.getDestinationTypes().subscribe({
      next: (types) => {
        this.destinationTypes = [...types].sort((a, b) => {
          const nameA = a?.Name || '';
          const nameB = b?.Name || '';
          return nameA.localeCompare(nameB);
        });
        // Load destinations only after types are loaded
        this.loadDestinations();
      },
      error: (error) => {
        console.error('[destinations] type load failed', error);
        this.destinationTypes = [];
        // Load destinations anyway even if types fail
        this.loadDestinations();
      }
    });
    this.subscriptions.add(sub);
  }

  loadDestinations(): void {
    this.isLoading = true;
    this.loadingError = null;
    const sub = this.destinationsApi.getDestinations().subscribe({
      next: (list) => {
        const mapped = list.map(dto => this.mapDestination(dto));
        this.entityOwnerService.ensureOwners(mapped.map(d => ({ id: d.entityId })));
        this.destinations = this.filterDestinationsByDcr(mapped);
        this.resetPagination();
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('[destinations] load failed', error);
        this.loadingError = error?.message || this.translate.instant('destinations.errors.load');
        this.destinations = [];
        this.filteredDestinations = [];
        this.isLoading = false;
      }
    });
    this.subscriptions.add(sub);
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredDestinations = this.destinations.filter(dest => {
      const matchesTerm =
        !term ||
        this.matchesSearch(dest, term);
      const matchesStatus = this.statusFilters[dest.status];
      return matchesTerm && matchesStatus;
    });
    this.ensureValidPage();
  }

  toggleStatusFilter(status: DestinationStatus): void {
    this.statusFilters = {
      ...this.statusFilters,
      [status]: !this.statusFilters[status]
    };
    this.resetPagination();
    this.applyFilters();
  }

  get statusCounts(): Record<DestinationStatus, number> {
    const normalizedSearch = this.searchTerm.toLowerCase().trim();
    const counts: Record<DestinationStatus, number> = {
      active: 0,
      inactive: 0
    };
    for (const destination of this.destinations) {
      if (this.matchesSearch(destination, normalizedSearch)) {
        counts[destination.status] += 1;
      }
    }
    return counts;
  }

  private matchesSearch(destination: DestinationView, normalizedSearch: string): boolean {
    if (!normalizedSearch) {
      return true;
    }
    const haystack = `${destination.id} ${destination.name} ${destination.typeName} ${destination.typeDescription || ''} ${destination.connectorName || ''} ${destination.activationType || ''} ${destination.entity}`.toLowerCase();
    return haystack.includes(normalizedSearch);
  }

  private createStatusFilters(): Record<DestinationStatus, boolean> {
    return {
      active: true,
      inactive: false
    };
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    saveSearch('reach/destinations', term);
    this.resetPagination();
    this.applyFilters();
  }

  get hasGlobalDataScope(): boolean {
    return this.authService.hasGlobalDataScope();
  }

  get ownerEntityOptions(): { value: string; label: string }[] {
    const dcrs = this.currentUser?.availableDCRs ?? [];
    return dcrs.map(dcr => ({
      value: dcr.id,
      label: dcr.Name || dcr.name || dcr.id
    }));
  }

  get paginatedDestinations(): DestinationView[] {
    const startIndex = this.currentPage * this.pageSize;
    return this.filteredDestinations.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    if (!this.filteredDestinations.length) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.filteredDestinations.length / this.pageSize));
  }

  changePageSize(size: Event | string | number): void {
    let numeric: number;
    if (size instanceof Event) {
      const target = size.target as HTMLSelectElement | null;
      numeric = target ? parseInt(target.value, 10) : this.pageSize;
    } else if (typeof size === 'string') {
      numeric = parseInt(size, 10);
    } else {
      numeric = size;
    }

    if (!Number.isFinite(numeric) || numeric <= 0 || numeric === this.pageSize) {
      return;
    }
    this.pageSize = numeric;
    this.resetPagination();
    this.applyFilters();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) {
      return;
    }
    this.currentPage = page;
  }

  openCreatePanel(): void {
    this.closeActionMenu();
    this.formMode = 'create';
    this.panelOpen = true;
    // Notify sidebar to disable entity selector
    this.creationStateService.setCreationMode(true);
    setTimeout(() => this.destinationFormComponent?.openCreate());
  }

  openEditPanel(item: DestinationView): void {
    this.closeActionMenu();
    this.formMode = 'edit';
    this.panelOpen = true;
    // Notify sidebar to disable entity selector
    this.creationStateService.setCreationMode(true);
    setTimeout(() => this.destinationFormComponent?.openEdit(item));
  }

  closePanel(): void {
    this.panelOpen = false;
    // Notify sidebar to enable entity selector
    this.creationStateService.setCreationMode(false);
  }

  handleFormSaved(): void {
    this.panelOpen = false;
    // Notify sidebar to enable entity selector
    this.creationStateService.setCreationMode(false);
    this.loadDestinations();
  }

  async deleteDestination(item: DestinationView): Promise<void> {
    if (!confirm(this.translate.instant('destinations.actions.deleteConfirm', { name: item.name }))) {
      return;
    }
    try {
      await firstValueFrom(this.destinationsApi.deleteDestination(item.id));
      this.notificationService.notify('success', this.translate.instant('destinations.messages.deleted'));
      this.loadDestinations();
    } catch (error) {
      console.error('[destinations] delete failed', error);
      this.notificationService.notify('error', (error as Error)?.message || this.translate.instant('destinations.errors.delete'));
    }
    this.closeActionMenu();
  }

  async toggleActivation(item: DestinationView): Promise<void> {
    const nextStatus: DestinationStatus = item.status === 'active' ? 'inactive' : 'active';
    const apiStatus = nextStatus === 'active' ? 1 : 0;
    try {
      await firstValueFrom(this.destinationsApi.updateDestination({ ID: item.id, IsActive: apiStatus }));
      item.status = nextStatus;
      const messageKey = nextStatus === 'active' ? 'destinations.messages.activated' : 'destinations.messages.deactivated';
      this.notificationService.notify('success', this.translate.instant(messageKey, { name: item.name }));
      this.applyFilters();
    } catch (error) {
      console.error('[destinations] status toggle failed', error);
      this.notificationService.notify('error', this.translate.instant('destinations.errors.status'));
    }
    this.closeActionMenu();
  }

  toggleActionsMenu(item: DestinationView, event: Event): void {
    event.stopPropagation();
    const isOpen = this.activeActionMenuId === item.id;
    if (isOpen) {
      this.closeActionMenu();
      return;
    }
    this.activeActionMenuId = item.id;
    this.activeActionItem = item;
    this.updateActionMenuPosition(event.currentTarget as HTMLElement);
  }

  clearSearch(): void {
    if (!this.searchTerm) {
      return;
    }
    this.searchTerm = '';
    saveSearch('reach/destinations', '');
    this.resetPagination();
    this.applyFilters();
  }

  getConnectionStatusClass(status?: string | null): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'ok') {
      return 'is-ok';
    }
    if (normalized === 'error') {
      return 'is-error';
    }
    return 'is-pending';
  }

  private resetPagination(): void {
    this.currentPage = 0;
  }

  private ensureValidPage(): void {
    if (!this.filteredDestinations.length) {
      this.currentPage = 0;
      return;
    }
    const totalPages = Math.max(1, Math.ceil(this.filteredDestinations.length / this.pageSize));
    const lastPageIndex = Math.max(0, totalPages - 1);
    if (this.currentPage > lastPageIndex) {
      this.currentPage = lastPageIndex;
    }
  }

  private mapDestination(dto: DestinationDto): DestinationView {
    const destinationType = this.destinationTypes.find(t => t.ID === dto.DestinationTypeID);
    const isActive = (dto.IsActive ?? 1) === 1;
    return {
      id: dto.ID,
      destinationTypeId: dto.DestinationTypeID,
      name: dto.Name,
      typeName: dto.DestinationTypeName,
      typeDescription: destinationType?.Description,
      connectorType: dto.ConnectorType,
      connectorName: dto.ConnectorName,
      activationType: dto.ActivationType,
      entity: this.resolveOwnerLabel(dto.EntityID, dto.EntityType),
      entityType: dto.EntityType,
      entityId: dto.EntityID,
      destinationGroup: dto.DestinationGroup || destinationType?.DestinationGroup,
      status: this.normalizeStatus(isActive),
      connectionStatus: dto.Status || 'OK'
    };
  }

  private normalizeStatus(isActive: boolean): DestinationStatus {
    return isActive ? 'active' : 'inactive';
  }

  private resolveOwnerLabel(entityId?: string | null, entityType?: string | null): string {
    const key = this.buildOwnerKey(entityType, entityId);
    if (key) {
      const mapped = this.ownerLabels.get(key);
      if (mapped) {
        return mapped;
      }
    }
    const normalizedId = (entityId ?? '').toString().trim();
    if (normalizedId) {
      const friendly = this.authService.getDcrNameById(normalizedId);
      if (friendly) {
        return friendly;
      }
    }
    if (normalizedId && entityType) {
      return `${entityType} • ${normalizedId}`;
    }
    return normalizedId || entityType || '-';
  }

  private filterDestinationsByDcr(destinations: DestinationView[]): DestinationView[] {
    const filterId = this.getActiveDcrFilter();
    if (!filterId) {
      return destinations;
    }
    return destinations.filter(dest => this.normalizeEntityId(dest.entityId) === filterId);
  }

  /** Single source of truth for the entity-selector scope (see AuthService.getEntityScopeFilterId). */
  private getActiveDcrFilter(): string | null {
    return this.authService.getEntityScopeFilterId();
  }

  private normalizeEntityId(value?: string | null): string {
    return (value ?? '').toString().trim().toLowerCase();
  }

  private buildOwnerKey(entityType?: string | null, entityId?: string | null): string | null {
    const type = (entityType ?? '').toString().trim().toLowerCase();
    const id = (entityId ?? '').toString().trim();
    if (!type || !id) {
      return null;
    }
    return `${type}::${id}`;
  }

  private closeActionMenu(): void {
    this.activeActionMenuId = null;
    this.activeActionItem = null;
    this.actionMenuArrowOffset = 24;
  }

  private updateActionMenuPosition(trigger: HTMLElement): void {
    if (typeof window === 'undefined') {
      this.actionMenuPosition = { top: 0, left: 0 };
      this.actionMenuArrowOffset = 24;
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 240;
    const padding = 16;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const preferredLeft = rect.right - menuWidth;
    const maxLeft = viewportWidth - menuWidth - padding;
    const left = Math.min(Math.max(preferredLeft, padding), maxLeft);
    this.actionMenuPosition = {
      top: rect.bottom + 8,
      left
    };

    const buttonCenter = rect.left + rect.width / 2;
    const arrowPadding = 24;
    const relativeCenter = buttonCenter - left;
    const clampedOffset = Math.min(Math.max(relativeCenter, arrowPadding), menuWidth - arrowPadding);
    this.actionMenuArrowOffset = clampedOffset;
  }

}
